// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// RxJS
import { Observable, of, throwError } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel } from '../../_base/crud';
import { ContactRelationshipModel } from '../_models/contact-relationship.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';
import { uniqWith, isEqual } from 'lodash-es';
import { catchError, map } from 'rxjs/operators';
import { ContactModel } from '../_models/contact.model';
import { RelationshipCategory } from '../_models/relationship-category.model';
import { RelationshipType } from '../_models/relationship-type.model';
import { EntityType } from '../../../views/pages/documents-management/enums';
import { Entity, EntityRelationFilter } from '../../../views/pages/documents-management/models';

// Real REST API
@Injectable({
  providedIn: 'root',
})
export class ContactRelationshipsService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  createContactRelationship(
    contactRelationship,
    contact,
    agreement?,
  ): Observable<ContactRelationshipModel> {
    // Note: Add headers if needed (tokens/bearer)
    agreement = agreement !== null && Object.keys(agreement).length === 0 ? null : agreement;

    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/' +
      contact.id +
      '/relations';
    const body = {
      entity_id: contactRelationship.id,
      entity_type: contactRelationship.entity_type,
      relationship_description: contactRelationship.relationship_description,
      relation_type: contactRelationship.relation_type,
      relation_type_id: contactRelationship.relation_type_id,
      is_prime: contactRelationship.is_prime,
      title: contactRelationship.title,
      phi_authorized: contactRelationship.phi_authorized,
      emergency_contact: contactRelationship.emergency_contact,
      financial_responsible: contactRelationship.financial_responsible,
      valid_until_revoked: contactRelationship.valid_until_revoked,
      authorized_upto_date: contactRelationship.authorized_upto_date,
      agreement: agreement,
    };
    return this.http.post<ContactRelationshipModel>(url, body, { headers: httpHeaders });
  }

  createEntityRelationship(contactRelationship, contact_id): Observable<ContactRelationshipModel> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/' +
      contact_id +
      '/relations';
    const body = {
      entity_id: contactRelationship.entity_id,
      entity_type: contactRelationship.entity_type,
      relationship_description: contactRelationship.relationship_description,
      relation_type: contactRelationship.relation_type,
      relation_type_id: contactRelationship.relation_type_id,
      is_prime: contactRelationship.is_prime,
      title: contactRelationship.title,
      phi_authorized: contactRelationship.phi_authorized,
      emergency_contact: contactRelationship.emergency_contact,
      financial_responsible: contactRelationship.financial_responsible,
      relation_classification: contactRelationship.relation_classification,
      valid_until_revoked: contactRelationship.valid_until_revoked,
      authorized_upto_date: contactRelationship.authorized_upto_date,
    };
    return this.http.post<ContactRelationshipModel>(url, body, { headers: httpHeaders });
  }

  // READ
  // Server should return filtered relationships by contactId
  getAllContactRelationshipsByContactId(contactId: number): Observable<ContactRelationshipModel[]> {
    const url = environment.CORE_BASE + '?contactId=' + contactId;
    return this.http.get<ContactRelationshipModel[]>(url);
  }

  getContactRelationshipById(contactRelationshipId: number): Observable<ContactRelationshipModel> {
    return this.http.get<ContactRelationshipModel>(
      environment.CORE_BASE + `/${contactRelationshipId}`,
    );
  }

  // Server should return sorted/filtered relationships and merge with items from state
  findContactRelationships(
    contactId: number,
    type = '',
    filteredView = false,
    queryParams = new QueryParamsModel('', 'asc', 'id', 0, 10),
  ): Observable<any> {
    const httpParams = this.httpUtils.getFindHTTPParams(queryParams);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/' +
      contactId +
      `/relations?relation_type=${type}${filteredView ? '&focus_view=true' : ''}`;
    // Note: Add headers if needed (tokens/bearer)
    if (!contactId) {
      return of();
    }
    return this.http.get<any>(url, { params: httpParams });
  }

  findContactRelationshipsImplicit(
    contactId: number,
    type = '',
    filteredView = false,
    queryParams = new QueryParamsModel('', 'asc', 'id', 0, 10),
  ): Observable<any> {
    const httpParams = this.httpUtils.getFindHTTPParams(queryParams);
    let url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/' +
      contactId +
      `/related_patients`;
    if (type) {
      url += `?type=${type}`;
    }
    if (filteredView) {
      url += type ? '&filtered=true' : '?filtered=true';
    }
    if (!contactId) {
      return of();
    }
    return this.http.get<any>(url, { params: httpParams });
  }

  // Server should return sorted/filtered relationships and merge with items from state
  findDoctorRelationships(contactId: number): Observable<any> {
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/entities/' +
      contactId +
      '/relations';
    // Note: Add headers if needed (tokens/bearer)
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({ fromString: 'term=' });
    return this.http.get<any>(url, { headers, params });
  }

  searchPatientRelations(patientId: number, term = ''): Observable<any> {
    return this.http.get<any>(
      `${environment.CORE_BASE}/companies/${this.companyId}/entities/${patientId}/relations?term=${term}`,
    );
  }

  // UPDATE => PUT: update the contact relationship
  updateContactRelationship(
    contactRelationship: ContactRelationshipModel,
    agreement,
  ): Observable<any> {
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/relations/' +
      contactRelationship.id;
    const body = {
      entity_id: contactRelationship.entity_id,
      entity_type: contactRelationship.entity_type,
      contact_id: contactRelationship.contact_id,
      relationship_description: contactRelationship.relationship_description,
      relation_type: contactRelationship.relation_type,
      relation_type_id: contactRelationship.relation_type_id,
      is_prime: contactRelationship.is_prime,
      title: contactRelationship.title,
      phi_authorized: contactRelationship.phi_authorized,
      valid_until_revoked: contactRelationship.valid_until_revoked,
      authorized_upto_date: contactRelationship.authorized_upto_date,
      emergency_contact: contactRelationship.emergency_contact,
      financial_responsible: contactRelationship.financial_responsible,
      agreement: agreement,
    };
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the contact relationship
  updateDoctorRelationship(contactRelationship: ContactRelationshipModel): Observable<any> {
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/relations/' +
      contactRelationship.id;
    const body = {
      relationship_description: contactRelationship.relationship_description,
      relation_type: contactRelationship.relation_type_id,
      relation_type_id: contactRelationship.relation_type_id,
    };
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // DELETE => delete the contact relationship
  deleteContactRelationship(contactRelationshipId: ContactRelationshipModel): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/relations/' +
      contactRelationshipId.id;
    return this.http.delete<any>(url, { headers: httpHeaders });
  }

  searchEntities(obj, term: string): Observable<any> {
    const relationType = obj.relationTypeId ? obj.relationTypeId : '';
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/entities/autocomplete?entityType=' +
      obj.type +
      '&term=' +
      term +
      relationType;
    return this.http.get<any>(url);
  }

  getEntities(id): Observable<any> {
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/categories/entities?category_ids=' +
      id;
    return this.http.get<any>(url);
  }

  getEntity(id): Observable<any> {
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/entities/' + id;
    return this.http.get<any>(url);
  }

  searchRelations(contactId: string): Observable<any> {
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts/' +
      contactId +
      `/relations`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchRelationsByContactIds(contactIds): Observable<any> {
    let ids = '';
    if (contactIds.length && contactIds.length === 0) {
      return of([]);
    }
    // tslint:disable-next-line:no-shadowed-variable
    contactIds = uniqWith(contactIds, isEqual);
    contactIds.forEach((element) => (ids = ids + '&contact_id=' + element));
    const params = new HttpParams({ fromString: ids });
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/contacts/relations';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders, params });
  }

  searchEntityRelationByField(
    type: string,
    contactId?: any,
    fromAffi?: boolean,
    term = '',
    field?: string,
  ): Observable<any> {
    switch (type) {
      case 'FACILITY':
        type = 'FACILITIE';
        break;
      case 'PHARMACY':
        type = 'PHARMACIE';
    }
    if (fromAffi) {
      return this.http.get<any>(
        `${environment.CORE_BASE}/companies/${
          this.companyId
        }/${type.toLowerCase()}s?page=0&size=40&sort=lastName,asc${
          field ? `&${field}=${term}` : `&search_term=${term}`
        }${contactId ? `&not_related_to_affiliation=${contactId}` : ''}`,
      );
    } else {
      if (type === 'AFFILIATION_GROUP') {
        return this.http.get<any>(
          `${environment.CORE_BASE}/companies/${
            this.companyId
          }/${type.toLowerCase()}s?page=0&size=40&sort=name,asc${
            field ? `&${field}=${term}` : `&search_term=${term}`
          }${contactId ? `&not_related_to=${contactId}` : ''}`,
        );
      } else
        return this.http.get<any>(
          `${environment.CORE_BASE}/companies/${
            this.companyId
          }/${type.toLowerCase()}s?page=0&size=40&sort=lastName,asc${
            field ? `&${field}=${term}` : `&search_term=${term}`
          }${contactId ? `&not_related_to=${contactId}` : ''}`,
        );
    }
  }

  // Server should return sorted/filtered relationships and merge with items from state
  searchLists(type: string, term: string): Observable<any> {
    // tslint:disable-next-line:max-line-length

    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/' +
      type.toLowerCase() +
      's?is_active=true&search_term=' +
      term;
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchEntityByField(
    type: string,
    term = '',
    field?: string,
    showActive?: boolean,
  ): Observable<any> {
    switch (type) {
      case 'FACILITY':
        type = 'FACILITIE';
        break;

      case 'PHARMACY':
        type = 'PHARMACIE';
    }
    return this.http.get<any>(
      `${environment.CORE_BASE}/companies/${
        this.companyId
      }/${type.toLowerCase()}s?page=0&size=40&sort=lastName,asc${
        field ? `&${field}=${term}` : `&search_term=${term}`
      }${showActive ? '&is_active=true' : ''}`,
    );
  }

  searchActiveEntityByField(
    type: string,
    contactId?: any,
    fromAffi?: boolean,
    term = '',
    field?: string,
  ): Observable<any> {
    if (fromAffi) {
      return this.searchEntityRelationByField(type, contactId, true, term, field);
    } else {
      return this.searchEntityRelationByField(type, contactId, false, term, field);
    }
  }

  getRelationshipTypes(type: string): Observable<any> {
    // tslint:disable-next-line:max-line-length
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/relation_types?entity_type=' +
      type;
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchRelationType(searchTerm: string): Observable<any> {
    return this.http
      .get<any>(
        'https://browser.ihtsdotools.org/snowstorm/snomed-ct/browser/MAIN/2020-07-31/descriptions?&limit=10&term=' +
          searchTerm +
          '&active=true&conceptActive=true&lang=english&semanticTags=person',
      )
      .pipe(
        map((res) => {
          return res.items;
        }),
      );
  }

  getRelationTypeDescription(type, category): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/relation_types?entity_type=' +
      type +
      '&category_id=' +
      category.id;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getRelationTypesCategory(type): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/relation_types?entity_type=' +
      type;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getRelations(
    entityTypes: EntityType[],
    relationCategories?: RelationshipCategory[],
  ): Observable<RelationshipCategory[] | RelationshipType[]> {
    let fromString = '';

    entityTypes?.length
      ? entityTypes.forEach((type) => (fromString += `&entity_type=${type}`))
      : (fromString += '&entity_type=');
    if (relationCategories?.length)
      relationCategories.forEach((category) => (fromString += `&category_id=${category.id}`));

    const params = new HttpParams({ fromString });

    return this.http.get<RelationshipCategory[] | RelationshipType[]>(
      `${environment.CORE_BASE}/companies/${this.companyId}/relation_types`,
      {
        params,
      },
    );
  }

  getEntitiesById(entityIds: number[]): Observable<{ data: Entity[] }> {
    let fromString = '';

    entityIds?.forEach((id) => (fromString += `&id=${id}`));

    const params = new HttpParams({ fromString });

    return this.http.get<{ data: Entity[] }>(
      `${environment.CORE_BASE}/companies/${this.companyId}/general_entities`,
      { params },
    );
  }

  findEntities(filter, searchTerm = ''): Observable<Entity[]> {
    let fromString = `term=${searchTerm}`;

    if (filter.entityTypes?.length)
      filter.entityTypes.forEach((type) => (fromString += `&entityType=${type}`));
    if (filter.relatedContactsIds?.length)
      filter.relatedContactsIds.forEach((id) => (fromString += `&related_contact_id=${id}`));

    const params = new HttpParams({ fromString });

    return this.http.get<Entity[]>(
      `${environment.CORE_BASE}/companies/${this.companyId}/entities/autocomplete`,
      { params },
    );
  }

  findEntitiesRelationsAutocomplete(
    filter: Partial<EntityRelationFilter>,
    searchTerm: string,
  ): Observable<ContactModel[]> {
    let fromString = '';

    if (filter.entityIds) filter.entityIds.forEach((id) => (fromString += `&entity_id=${id}`));
    if (filter.relationCategoryIds)
      filter.relationCategoryIds.forEach((id) => (fromString += `&relation_category_id=${id}`));
    if (filter.relationTypeIds)
      filter.relationTypeIds.forEach((id) => (fromString += `&relation_type_id=${id}`));
    if (filter.phi) fromString += `&phi=${filter.phi}`;
    if (filter.patientOnly) fromString += `&patient_only=${filter.patientOnly}`;
    if (filter.invoice) fromString += `&invoice=${filter.invoice}`;
    if (filter.hasEmail) fromString += `&has_email=${filter.hasEmail}`;
    if (filter.hasFax) fromString += `&has_fax=${filter.hasFax}`;
    if (searchTerm) fromString += `&term=${searchTerm}`;

    const params = new HttpParams({ fromString });

    return this.http.get<ContactModel[]>(
      `${environment.CORE_BASE}/companies/${this.companyId}/entities/all-relations`,
      { params },
    );
  }

  /**
   * Fetch the billing contact for the given company and entity IDs.
   * @param entityIds The list of entity IDs.
   * @returns An observable of the response.
   */
  getBillingContact(entityIds: number[]): Observable<any> {
    const url = `${environment.CORE_BASE}/companies/${this.companyId}/contacts/billing-contact`;
    const body = { entity_ids: entityIds };

    return this.http.post<any>(url, body).pipe(catchError(this.handleError));
  }

  private handleError(error): Observable<never> {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      errorMessage = `Server-side error: ${error.status} - ${error.error.message || error.message}`;
    }
    return throwError(errorMessage);
  }
}
